// ===========================================
// Extra Lesson Settings Tab Component
// ===========================================
// Admin UI for configuring extra lesson booking behavior

import { useEffect } from 'react';
import {
  Box,
  Button,
  Card,
  CardContent,
  CardHeader,
  FormControl,
  FormControlLabel,
  FormLabel,
  Radio,
  RadioGroup,
  Skeleton,
  Stack,
  Switch,
  TextField,
  Typography,
  Alert,
} from '@mui/material';
import { Controller, useForm } from 'react-hook-form';
import {
  useExtraLessonSettings,
  useUpdateExtraLessonSettings,
  useResetExtraLessonSettings,
} from '../../../hooks/useAdminSettings';
import type { ExtraLessonSettingsInput } from '../../../types/adminSettings.types';
import SettingsWriteGate from './SettingsWriteGate';

interface FormData {
  enabled: boolean;
  bookingType: 'REQUEST' | 'DIRECT';
  defaultDurationMinutes: number;
  maxRequestsPerStudentPerTerm: string; // String for form, convert to number
  allowParentSelectTeacher: boolean;
}

export default function ExtraLessonSettingsTab() {
  const { data: settings, isLoading, error } = useExtraLessonSettings();
  const updateMutation = useUpdateExtraLessonSettings();
  const resetMutation = useResetExtraLessonSettings();

  const {
    control,
    handleSubmit,
    reset,
    watch,
    formState: { isDirty },
  } = useForm<FormData>({
    defaultValues: {
      enabled: true,
      bookingType: 'REQUEST',
      defaultDurationMinutes: 60,
      maxRequestsPerStudentPerTerm: '',
      allowParentSelectTeacher: true,
    },
  });

  const bookingType = watch('bookingType');
  const enabled = watch('enabled');

  // Populate form when data loads
  useEffect(() => {
    if (settings) {
      reset({
        enabled: settings.enabled,
        bookingType: settings.bookingType,
        defaultDurationMinutes: settings.defaultDurationMinutes,
        maxRequestsPerStudentPerTerm: settings.maxRequestsPerStudentPerTerm?.toString() || '',
        allowParentSelectTeacher: settings.allowParentSelectTeacher,
      });
    }
  }, [settings, reset]);

  const onSubmit = (data: FormData) => {
    const payload: ExtraLessonSettingsInput = {
      enabled: data.enabled,
      bookingType: data.bookingType,
      defaultDurationMinutes: data.defaultDurationMinutes,
      maxRequestsPerStudentPerTerm: data.maxRequestsPerStudentPerTerm
        ? parseInt(data.maxRequestsPerStudentPerTerm, 10)
        : null,
      allowParentSelectTeacher: data.allowParentSelectTeacher,
    };

    updateMutation.mutate(payload);
  };

  const handleReset = () => {
    if (window.confirm('Reset extra lesson settings to defaults? This cannot be undone.')) {
      resetMutation.mutate();
    }
  };

  if (isLoading) {
    return (
      <Stack spacing={3}>
        <Skeleton variant="rectangular" height={200} />
        <Skeleton variant="rectangular" height={300} />
      </Stack>
    );
  }

  if (error) {
    return <Alert severity="error">Failed to load settings: {error.message}</Alert>;
  }

  return (
    <Box component="form" onSubmit={handleSubmit(onSubmit)}>
      <Stack spacing={3}>
        {/* Master Enable/Disable */}
        <Card variant="outlined">
          <CardHeader
            title="Extra Lesson Bookings"
            subheader="Control whether parents can book extra lessons"
          />
          <CardContent>
            <Controller
              name="enabled"
              control={control}
              render={({ field }) => (
                <FormControlLabel
                  control={<Switch {...field} checked={field.value} />}
                  label={field.value ? 'Enabled' : 'Disabled'}
                />
              )}
            />
            {!enabled && (
              <Alert severity="warning" sx={{ mt: 2 }}>
                Extra lesson bookings are disabled. Parents cannot submit requests or book lessons.
              </Alert>
            )}
          </CardContent>
        </Card>

        {/* Booking Type */}
        {enabled && (
          <Card variant="outlined">
            <CardHeader
              title="Booking Workflow"
              subheader="Choose how parents book extra lessons"
            />
            <CardContent>
              <Controller
                name="bookingType"
                control={control}
                render={({ field }) => (
                  <FormControl component="fieldset">
                    <FormLabel component="legend">Booking Type</FormLabel>
                    <RadioGroup {...field}>
                      <FormControlLabel
                        value="REQUEST"
                        control={<Radio />}
                        label={
                          <Box>
                            <Typography variant="body1" fontWeight="medium">
                              Request & Approval
                            </Typography>
                            <Typography variant="body2" color="text.secondary">
                              Parent submits request → Admin approves → Admin schedules with teacher
                            </Typography>
                          </Box>
                        }
                      />
                      <FormControlLabel
                        value="DIRECT"
                        control={<Radio />}
                        label={
                          <Box>
                            <Typography variant="body1" fontWeight="medium">
                              Direct Booking
                            </Typography>
                            <Typography variant="body2" color="text.secondary">
                              Parent selects teacher and time → Automatically confirmed
                            </Typography>
                          </Box>
                        }
                      />
                    </RadioGroup>
                  </FormControl>
                )}
              />
            </CardContent>
          </Card>
        )}

        {/* Direct Booking Options */}
        {enabled && bookingType === 'DIRECT' && (
          <Card variant="outlined">
            <CardHeader
              title="Direct Booking Options"
              subheader="Configure direct booking behavior"
            />
            <CardContent>
              <Controller
                name="allowParentSelectTeacher"
                control={control}
                render={({ field }) => (
                  <FormControlLabel
                    control={<Switch {...field} checked={field.value} />}
                    label={
                      <Box>
                        <Typography variant="body1">Allow Parent to Select Teacher</Typography>
                        <Typography variant="body2" color="text.secondary">
                          Parent chooses from available teachers. If disabled, uses student's
                          current teacher. Time slots are always checked to prevent double-booking.
                        </Typography>
                      </Box>
                    }
                  />
                )}
              />
            </CardContent>
          </Card>
        )}

        {/* General Options */}
        {enabled && (
          <Card variant="outlined">
            <CardHeader
              title="General Options"
              subheader="Default settings for extra lessons"
            />
            <CardContent>
              <Stack spacing={3}>
                <Controller
                  name="defaultDurationMinutes"
                  control={control}
                  rules={{
                    required: 'Duration is required',
                    min: { value: 15, message: 'Minimum 15 minutes' },
                    max: { value: 180, message: 'Maximum 180 minutes' },
                  }}
                  render={({ field, fieldState: { error } }) => (
                    <TextField
                      {...field}
                      label="Default Lesson Duration (minutes)"
                      type="number"
                      fullWidth
                      error={!!error}
                      helperText={error?.message || 'Default: 60 minutes'}
                      inputProps={{ min: 15, max: 180, step: 15 }}
                    />
                  )}
                />

                <Controller
                  name="maxRequestsPerStudentPerTerm"
                  control={control}
                  render={({ field }) => (
                    <TextField
                      {...field}
                      label="Max Requests Per Student Per Term"
                      type="number"
                      fullWidth
                      helperText="Leave empty for unlimited"
                      inputProps={{ min: 1, max: 50 }}
                    />
                  )}
                />
              </Stack>
            </CardContent>
          </Card>
        )}

        {/* Action Buttons */}
        <SettingsWriteGate>
          <Stack direction="row" spacing={2} justifyContent="flex-end">
            <Button variant="outlined" onClick={handleReset} disabled={resetMutation.isPending}>
              Reset to Defaults
            </Button>
            <Button
              type="submit"
              variant="contained"
              disabled={!isDirty || updateMutation.isPending}
            >
              {updateMutation.isPending ? 'Saving...' : 'Save Changes'}
            </Button>
          </Stack>
        </SettingsWriteGate>
      </Stack>
    </Box>
  );
}
